<template>
    <transition name="fade">
      <div v-if="visible" class="fixed" @click.stop>
        <div class="bg-white popup">
          <div class="popup-header">
            <h1 class="title">{{ props.planName }}</h1>
            <img src="@/assets/images/ic_close.svg" class="close_icon" @click="closePopup()" />
          </div>
          <div class="popup-body">
            <h2>Listado de comentarios</h2>
          <table style="width: 100%;">
            <thead>
              <tr>
                  <th style="text-align: left;" class="small" colspan="2">Usuario</th>
                  <th class="small">Comentario</th>
                  <th class="small">Acciones</th>
              </tr>
            </thead>
            <tr v-for="(comment) in comments" :key="comment.id_comentario" style="width: 100%;">
              <td class="small"  style="text-align: center;">
                <img
                  :src="comment.imagen || defaultAvatar"
                  alt="Imagen del participante"
                  style="height: 40px; width: 40px; border-radius: 50%"
                />
              </td>
              <td class="small">&nbsp;{{ comment.nombre_usuario }}</td>
              <td class="big">{{ comment.comentario }}</td>
              <td class="small" style="text-align: center;">
                <img class="action_icon" src="@/assets/images/ic_action_delete.png" @click="onDeleteClicked(comment)"/>
              </td>
            </tr>
          </table>
          </div>
        </div>
        <TwoButtonPopup
          :title="twoButtonTitle"
          :message="twoButtonMessage"
          :acceptButton="twoButtonAcceptButton"
          @acceptCallback="() => twoButtonConfirmed()"
          :cancelButton="twoButtonCancelButton"
          @cancelCallback="() => twoButtonCancelled()"
          />
      </div>
    </transition>
  </template>
  
  <script setup>
  import { ref, watch, defineProps, nextTick } from 'vue';
  import TwoButtonPopup from "@/components/common/TwoButtonPopup.vue";
  import { useLoadingStore } from "@/stores/loadingStore";
  import { useRouter } from 'vue-router';
  import { refreshToken } from "@/services/authService";
  import defaultAvatar from '@/assets/images/ic_profile.png';

  const props = defineProps({
    planName: String,
    planId: Number,
    comments: Array,
  });
  
  const loadingStore = useLoadingStore();
  const router = useRouter();
  const error = ref(null);

  const visible = ref(false);
  const comments = ref(null);
  const twoButtonTitle = ref("");
  const twoButtonMessage = ref("");
  const twoButtonAcceptButton = ref("");
  const twoButtonCancelButton = ref("");
  let twoButtonConfirmed = null;
  let twoButtonCancelled = null;

  watch(() => props.comments, (newVal) => {
    comments.value = newVal;
    visible.value = !!newVal;
  });
  
  const showError = async (message) => {
    error.value = null;
    await nextTick(); // Espera a que Vue reaccione al cambio
    error.value = message;
  };

  const closePopup = () => {
    comments.value = null;
    visible.value = false;
  };

  function onDeleteClicked(comment) {
    twoButtonTitle.value = "Eliminar comentario";
    twoButtonMessage.value = "¿Estás seguro de que quieres eliminar el comentario de '" + comment.nombre_usuario + "' del plan?";
    twoButtonAcceptButton.value = "Si, eliminar";
    twoButtonCancelButton.value = "Cancelar";
    twoButtonConfirmed = () => {
      twoButtonTitle.value = "";
      twoButtonMessage.value = "";
      twoButtonAcceptButton.value = "";
      twoButtonCancelButton.value = "";
      onDeleteConfirmed(comment);
    };
    twoButtonCancelled = () => {
      twoButtonTitle.value = "";
      twoButtonMessage.value = "";
      twoButtonAcceptButton.value = "";
      twoButtonCancelButton.value = "";
    }
  }

  const onDeleteConfirmed = async (item) => {
    loadingStore.setLoading(true);
    error.value = null;

    try {
      let response = await deleteCommentInAPI(item);

      if (response.status === 401) {
        const refreshed = await refreshToken();
        if (refreshed) {
          response = await deleteCommentInAPI();
        } else {
          loadingStore.setLoading(false);
          localStorage.removeItem('user_id');
          localStorage.removeItem('token');
          router.push('/');
          return;
        }
      }

      loadingStore.setLoading(false);
      
      if (!response.ok) {
        showError("Se ha producido un error eliminando el comentario del plan.");
        return;
      }

      const data = await response.json();

      if (data.errors) {
        showError(data.message);
      } else {
        comments.value = comments.value.filter(c => c.id_comentario !== item.id_comentario);
      }
    } catch (exception) {
      showError(exception.message);
    }
  };

  const deleteCommentInAPI = async (item) => {
    return await fetch(process.env.VUE_APP_API_URL+"api/god/plans/delete_comment_from_particular_plan", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "vgod": process.env.VUE_APP_API_VGOD,
          "cp-token": process.env.VUE_APP_API_CP_TOKEN,
          "Authorization": "Bearer " + localStorage.getItem('token')
        },
        body: JSON.stringify({
          "user_id": localStorage.getItem('user_id'),
          "comment_id": item.id_comentario,
          "plan_id": props.planId
        }),
      });
  };

  </script>
  
  <style scoped>
  .fade-enter-active, .fade-leave-active {
    transition: opacity 0.3s ease;
  }
  .fade-enter, .fade-leave-to {
    opacity: 0;
  }
  
  /* .fixed is global */
  
  .bg-white {
    background-color: white;
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-lg);
    width: 90%;
    max-width: 1200px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    max-height: 90vh;
  }
  
  .popup-header {
    padding: var(--spacing-md) var(--spacing-lg);
    border-bottom: 1px solid var(--light-gray);
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .title {
    font-size: 20px;
    font-weight: 700;
    color: var(--text-color-dark);
    margin: 0;
  }

  .close_icon {
    position: absolute;
    right: var(--spacing-md);
    top: 50%;
    transform: translateY(-50%);
    width: 24px;
    height: 24px;
    cursor: pointer;
    opacity: 0.6;
    transition: opacity var(--transition-fast);
  }

  .close_icon:hover {
    opacity: 1;
  }

  .popup-body {
    padding: var(--spacing-lg);
    overflow-y: auto;
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: var(--spacing-md);
  }

  .popup {
    position: relative;
  }

  .circular-image {
    width: 100px;
    height: 100px;
    border-radius: 50%;
    object-fit: cover;
    margin-right: 20px;
  }

  .user-info {
    display: flex;
    padding-top:10px;
    align-items: center;
  }

  .user-text {
    display: flex;
    flex-direction: column;
  }

  .user-text h2 {
    width: 100%;
  }
  .questions {
    margin-top: 15px;
    display: flex;
    flex-direction: column;
    border-top: 1px solid var(--very-clear-gray);
    padding-top: 15px;
  }

  .questions h3 {
    font-weight: normal;
  }

  .questions label {
    margin-top: 10px;
  }

  .products {
    margin-top: 15px;
    display: flex;
    flex-direction: column;
    border-top: 1px solid var(--very-clear-gray);
    padding-top: 15px;
  }

  .products h3 {
    font-weight: normal;
  }

  .button-disabled {
    opacity: 0.5;
  }

  .button-enabled {
    opacity: 1;
  }

  .title {
    text-align: center;
    margin-top: 0px;
    margin-bottom: 0px;
  }

  .subtitle {
    text-align: center;
  }

  .subtitle-codes {
    text-align: center;
    color: var(--clear-gray);
  }
  
  .subtitle-codes-mini {
    font-size: 12px;
    font-style: normal;
    font-weight: normal;
  }
  ::v-deep(.ql-container) {
    font-family: "GatterSans" !important;
    font-size: 16px !important;
  }

  ::v-deep(.ql-toolbar) {
    box-sizing: border-box;
    border: 1px solid var(--gray) !important;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
  }

  ::v-deep(.ql-editor) {
    box-sizing: border-box;
    border-left: 1px solid var(--gray) !important;
    border-right: 1px solid var(--gray) !important;
    border-bottom: 1px solid var(--gray) !important;
    border-top: 0px;
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
  }

  ::v-deep(.ql-snow) {
    border: 0px;
  }

  /* Modern Flex Grid (gap-based, no margin hacks) */
  .fields {
    display: flex;
    flex-wrap: wrap;
    gap: var(--spacing-md);
    margin-bottom: var(--spacing-sm);
  }

  .input-25  { flex: 1 1 calc(25% - var(--spacing-md)); min-width: 150px; }
  .input-33  { flex: 1 1 calc(33.33% - var(--spacing-md)); min-width: 200px; }
  .input-50  { flex: 1 1 calc(50% - var(--spacing-md)); min-width: 260px; }
  .input-66  { flex: 1 1 calc(66.66% - var(--spacing-md)); min-width: 320px; }
  .input-100 { flex: 1 1 100%; }

  .input-25 input, .input-25 select,
  .input-33 input, .input-33 select,
  .input-50 input, .input-50 select,
  .input-66 input, .input-66 select,
  .input-100 input, .input-100 select, .input-100 textarea {
    width: 100%;
    box-sizing: border-box;
  }

  .gallery-input-33 {
    flex: 1 1 calc(33.33% - var(--spacing-md));
    min-width: 180px;
    position: relative;
  }

  .gallery-input-33 img {
    border-radius: var(--radius-md);
  }

  .datepicker-container {
    width: 100%;
    display: flex;
    justify-content: center;
    margin-bottom: var(--spacing-sm);
    box-sizing: border-box;
    border-radius: var(--radius-md);
    border: 1px solid var(--border-color);
    background-color: var(--white);
  }

  .content-preview {
    margin-top: var(--spacing-md);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-md);
    padding: var(--spacing-md);
    background-color: var(--very-clear-gray);
  }

  .input-checkbox {
    display: flex;
    align-items: center;
    margin-top: var(--spacing-sm);
  }

  .checkbox-label {
    display: flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
  }

  .fields input:disabled {
    background-color: var(--very-clear-gray) !important;
    border-color: var(--border-color-subtle) !important;
    color: var(--text-color-placeholder);
  }

  .expiration-date {
    box-sizing: border-box;
    border-radius: var(--radius-md);
    border: 1px solid var(--border-color);
    background-color: var(--white);
  }

  .expiration-date-container {
    width: 100%;
    margin-top: 4px;
    display: flex;
    justify-content: center;
  }

  .image-gallery {
    width: 100%;
    aspect-ratio: 1;
    object-fit: cover;
    background-color: var(--very-clear-gray);
    border-radius: var(--radius-md);
    border: 1px dashed var(--border-color);
    display: block;
    cursor: pointer;
    transition: border-color var(--transition-fast);
  }

  .image-gallery:hover {
    border-color: var(--primary-color);
  }

  .image-gallery-delete {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 28px;
    height: 28px;
    cursor: pointer;
    border-radius: 50%;
    background-color: var(--white);
    box-shadow: var(--shadow-sm);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 4px;
    box-sizing: border-box;
  }

  .add_button {
    padding-top: var(--spacing-md);
    cursor: pointer;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 6px;
    font-weight: 600;
    color: var(--text-color-dark);
    transition: opacity var(--transition-fast);
  }

  .add_button:hover {
    opacity: 0.75;
  }

  .without-top-padding {
    padding-top: 0 !important;
  }

  .centered {
    justify-content: center !important;
  }

  .campo-pedir-icon {
    width: 20px;
    height: 20px;
    cursor: pointer;
    padding-top: 8px;
    padding-left: 8px;
    opacity: 0.7;
    transition: opacity var(--transition-fast);
  }

  .campo-pedir-icon:hover {
    opacity: 1;
  }
  </style>
  